// Choosing a station. There are 67 of them, so this is a search box and a
// list, not a wall of buttons: type a few letters, or take the one at the top
// because that is where the pods are.

import { useMemo, useRef, useState, useEffect } from 'react';
import { Sheet } from '@/components/ui/Sheet';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { useT } from '@/i18n/useT';
import { leadWithRoster, rosterStations } from '@/lib/stations';
import { useFlowlines, useWaitingCounts, useWorkcentres } from '@/queries';
import { useStore } from '@/store';

export function StationPickerSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const workcentreId = useStore((s) => s.workcentreId);
  const flowlineId = useStore((s) => s.flowlineId);
  const operatorId = useStore((s) => s.operator?.id ?? null);
  const setStation = useStore((s) => s.setStation);

  const workcentres = useWorkcentres();
  const flowlines = useFlowlines();
  const counts = useWaitingCounts(flowlineId);
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  // The operator's own stations — the ones whose roster names them. The
  // picker opens on those when there are any; everything else is one tap
  // away, and a search always looks across the lot.
  const mine = useMemo(() => rosterStations(workcentres.data ?? [], operatorId), [workcentres.data, operatorId]);
  const hasRoster = leadWithRoster(workcentres.data ?? [], operatorId);
  const [scope, setScope] = useState<'mine' | 'all'>('mine');

  // Clear the previous search each time it opens; the operator is usually
  // looking for something different.
  useEffect(() => {
    if (open) {
      setQuery('');
      setScope('mine');
    }
  }, [open]);

  const showingMine = hasRoster && scope === 'mine' && !query.trim();

  const rows = useMemo(() => {
    const wcs = showingMine ? mine : (workcentres.data ?? []);
    const waiting = (id: number) => counts.data?.[id] ?? 0;
    const q = query.trim().toLowerCase();
    const matched = q
      ? wcs.filter((w) => `${w.name} ${w.code ?? ''}`.toLowerCase().includes(q))
      : wcs;
    // Busiest first when browsing; by name once searching, since the operator
    // already knows what they are looking for.
    return [...matched].sort((a, b) =>
      q ? a.name.localeCompare(b.name) : waiting(b.id) - waiting(a.id) || a.name.localeCompare(b.name),
    );
  }, [workcentres.data, counts.data, query, mine, showingMine]);

  const choose = (w: { id: number; name: string }) => {
    const fl = (flowlines.data ?? []).find((f) => f.id === flowlineId);
    setStation({ id: w.id, name: w.name }, fl ? { id: fl.id, name: fl.name } : null);
    onClose();
  };

  return (
    <Sheet open={open} onClose={onClose} variant="tall" title={t.station}>
      <input
        ref={searchRef}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search stations…"
        aria-label="Search stations"
        // Opened for one reason: to type in it. Sheet focuses this on open.
        data-autofocus="true"
        autoComplete="off"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          width: '100%',
          height: 46,
          borderRadius: 12,
          background: 'var(--surface2)',
          border: '1.5px solid var(--line)',
          padding: '0 14px',
          font: '500 14px Manrope,sans-serif',
          color: 'var(--ink)',
          marginBottom: 12,
        }}
      />

      {hasRoster && !query.trim() && (
        <div role="tablist" style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {(
            [
              ['mine', `${t.myStationsT} · ${mine.length}`],
              ['all', t.allStationsT],
            ] as const
          ).map(([key, labelText]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={scope === key}
              className="press"
              onClick={() => setScope(key)}
              style={{
                flex: 1,
                minHeight: 40,
                borderRadius: 12,
                font: '600 12.5px Manrope,sans-serif',
                background: scope === key ? 'var(--accent)' : 'var(--surface2)',
                color: scope === key ? 'var(--accentInk)' : 'var(--ink)',
              }}
            >
              {labelText}
            </button>
          ))}
        </div>
      )}

      {workcentres.isPending ? (
        <div style={{ display: 'grid', gap: 8 }}>
          <Skeleton height={54} />
          <Skeleton height={54} />
          <Skeleton height={54} />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState>No station matches “{query}”</EmptyState>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((w) => {
            const waiting = counts.data?.[w.id] ?? 0;
            const active = w.id === workcentreId;
            return (
              <button
                key={w.id}
                type="button"
                className="press-row"
                onClick={() => choose(w)}
                aria-current={active ? 'true' : undefined}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '12px 14px',
                  borderRadius: 13,
                  background: active ? 'var(--soft)' : 'var(--surface2)',
                  border: `1.5px solid ${active ? 'var(--accent)' : 'transparent'}`,
                  width: '100%',
                }}
              >
                <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <span style={{ display: 'block', font: '600 13px Manrope,sans-serif' }}>{w.name}</span>
                  <span style={{ display: 'block', font: '500 10px Manrope,sans-serif', color: 'var(--sub)' }}>
                    {[w.isEol ? 'End of line' : null, w.code].filter(Boolean).join(' · ') || ' '}
                  </span>
                </span>
                <span
                  style={{
                    font: '600 11px Manrope,sans-serif',
                    color: waiting > 0 ? 'var(--accent)' : 'var(--sub)',
                    flex: 'none',
                  }}
                >
                  {waiting} {t.waiting}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </Sheet>
  );
}
